<!--
  Previous / next, with a position readout.

  It steps over `recordNav`, which is the same ordered list `RecordNavSidebar` renders — the
  queue's current page. That is the whole contract: a pager that walks a different order than
  the list beside it is worse than no pager, because nothing in the tool would ever reveal the
  disagreement.

  Neither end is silently inert. When a direction has nowhere to go the button is disabled *and*
  the reason is in the accessible tree (and, in the `bar` variant, on screen).
-->
<script lang="ts" module>
  // A module counter rather than a per-render hook: two pagers are mounted at once (the record
  // header and the collapsed rail), and each one's `aria-describedby` has to resolve to its own
  // paragraph rather than the other instance's.
  let counter = 0;
  function nextId(): number {
    counter += 1;
    return counter;
  }
</script>

<script lang="ts">
  import { blurOnPointerActivation } from '../lib/keymap';
  import type { NavDirection } from '../lib/nav';
  import { recordNav } from '../lib/stores/nav.svelte';

  interface Props {
    /** `bar` sits in the record header; `rail` is the collapsed sidebar's vertical strip. */
    variant?: 'bar' | 'rail';
  }

  const { variant = 'bar' }: Props = $props();

  const uid = nextId();
  const prevReasonId = `pager-prev-${uid}`;
  const nextReasonId = `pager-next-${uid}`;

  const position = $derived(recordNav.position);
  const imageAt = $derived(recordNav.imagePosition);
  const canPrev = $derived(recordNav.canGo(-1));
  const canNext = $derived(recordNav.canGo(1));
  const prevReason = $derived(recordNav.describe(-1));
  const nextReason = $derived(recordNav.describe(1));

  function move(direction: NavDirection, event: MouseEvent): void {
    blurOnPointerActivation(event);
    void recordNav.go(direction);
  }
</script>

<div class="pager" class:rail={variant === 'rail'} class:bar={variant === 'bar'}>
  <div class="controls">
    <button
      class="subtle step"
      onclick={(event) => move(-1, event)}
      disabled={!canPrev}
      aria-describedby={prevReasonId}
      title={prevReason}
      aria-label="Previous inscription"
    >
      <span aria-hidden="true">‹</span>
      {#if variant === 'bar'}<span class="word">Prev</span>{/if}
      <kbd>[</kbd>
    </button>
    <!--
      The position is the `bar` variant's alone.

      In the 44px collapsed rail "1 of 60" does not fit at any size worth reading, and it was
      spilling out of the strip along with the image count underneath it — a column of clipped
      fragments beside the canvas. The rail's job when collapsed is two arrows; the record
      header's `bar` pager is always mounted and carries the numbers, so nothing is lost by
      leaving them out here.
    -->
    {#if variant === 'bar'}
      <span class="position mono" aria-live="polite">
        {#if position}
          {position.index} of {position.total}
        {:else if recordNav.records.length === 0}
          <span class="muted">—</span>
        {:else}
          <span class="muted">off list</span>
        {/if}
      </span>
    {/if}
    <button
      class="subtle step"
      onclick={(event) => move(1, event)}
      disabled={!canNext}
      aria-describedby={nextReasonId}
      title={nextReason}
      aria-label="Next inscription"
    >
      <kbd>]</kbd>
      {#if variant === 'bar'}<span class="word">Next</span>{/if}
      <span aria-hidden="true">›</span>
    </button>
  </div>

  {#if imageAt && variant === 'bar'}
    <!-- The record is the unit of the list; the image is the unit of the canvas. When a stone
         has more than one photograph, say so, or "3 of 40" is quietly ambiguous. `bar` only,
         for the same reason as the position above. -->
    <p class="small muted imageat">image {imageAt.index} of {imageAt.total}</p>
  {/if}

  <!--
    Always in the DOM so `aria-describedby` resolves, and always visually hidden.

    These used to render on screen in the `bar` variant whenever a direction was blocked.
    That put "This is the first inscription in the queue (1 of 3446), so there is no previous
    one." permanently under the pager — twice over, since the sidebar and the record header
    each mount one. Sighted users already have that fact twice: the button is visibly
    disabled and the position readout next to it says `1 of 3446`. Prose restating it is
    noise, and permanent noise is what people learn to stop reading.

    Assistive tech still gets the full sentence via `aria-describedby`, where the disabled
    attribute alone would only convey "unavailable", not why.
  -->
  <p id={prevReasonId} class="small muted reason visually-hidden">{prevReason}</p>
  <p id={nextReasonId} class="small muted reason visually-hidden">{nextReason}</p>
</div>

<style>
  .pager {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .rail .controls {
    flex-direction: column;
    gap: 6px;
  }

  .step {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    border-color: var(--line);
  }

  .position {
    font-size: var(--text-xs);
    white-space: nowrap;
    text-align: center;
  }

  /* The rail renders no position or image count — see the template. */

  .reason,
  .imageat {
    margin: 0;
    max-width: 34ch;
  }

  .word {
    font-size: 0.8rem;
  }
</style>
